import { Component } from '@angular/core';
import { OnInit, OnDestroy, Input } from '@angular/core';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { SearchService } from '../../search/services/search.service';
import { AllFacetsComponent } from '../all-facets/all-facets.component';
import { Criterion } from '../../models/search-objects.interface';
import dayjs from 'dayjs';

interface Facet {
	name: string;
	dcname: string;
	displayName: string;
	solrfieldname: string;
	multiFaceted: boolean;
	valid: boolean;
	value: FacetValue[];
}

interface FacetValue {
	display: string;
	displayName: string;
	quotifiedValue: string;
	value: string;
	hide: boolean;
	checked: boolean;
	count: string;
}

/**
 * Component for refining a search using selected facet values.
 * Displays a truncated list of facet values provided from the search.
 */
@Component({
	selector: 'facets',
	templateUrl: './facets.component.html',
	styleUrl: './facets.component.css'
})
export class FacetsComponent implements OnInit, OnDestroy {
	@Input() data: any;

	private searchEventSubscription!: Subscription;
	private userServiceSubscription!: Subscription;

	LOCALIZATION: any;

	facets: Facet[] = [];

	maxLimit: number = 25;
	facetfilter: { [key: string]: string } = {};
	focused: { [key: string]: boolean } = {};
	facetVisibleCount: { [key: string]: number } = {};
	facetLimit: { [key: string]: number } = {};

	facetCriteria: any[] = [];
	multiFacetSearch: boolean = false;
	multiFacetBlacklist: any[] = [];
	searchInputs: any[] = [];
	criteria: { [key: string]: Criterion } | null = {};
	validFields: any[] = [];

	expandedStates: boolean[] = [];

	constructor(
		private ngbModal: NgbModal,
		private configService: ConfigService,
		private userService: UserService,
		private searchService: SearchService
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.searchInputs = this.userService?.user?.searchInputs || [];

		this.multiFacetSearch = this.configService.getConfig().search.multiFacetSearch || false;

		let multiFacetBlacklistStr =
			(this.configService.getConfig().search && this.configService.getConfig().search.multiFacetSearchBlacklist) ?
				this.configService.getConfig().search.multiFacetSearchBlacklist :
				"";

		this.multiFacetBlacklist = multiFacetBlacklistStr.split(',').map((item: any) => {
			return item.trim();
		});
		
		this.userServiceSubscription = this.userService.broadcastEvent.subscribe((eventId: string) => {
			if ("localizationLoaded" === eventId) {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			} 
		});
		
		this.searchEventSubscription = this.searchService.broadcastEvent.subscribe(event => {
			if (event.type === 'searchEnded') {
				this.listenToSearchEndedEvent(event.data);
			}
			else if (event.type === 'clearSearch') {
				this.facets = [];
				this.criteria = {};
			}
		});
		if(this.searchService.results){
			this.listenToSearchEndedEvent(this.searchService.results);
		}
	}

	ngOnDestroy() {
		if (this.userServiceSubscription) {
			this.userServiceSubscription.unsubscribe();
		}
		
		if (this.searchEventSubscription) {
			this.searchEventSubscription.unsubscribe();
		}
	}
	
	private listenToSearchEndedEvent(data: any): void {
		if (this.searchService.criteria) {
			this.facetCriteria = Object.keys(this.searchService.criteria);
		}

		this.criteria = this.searchService.criteria && Object.keys(this.searchService.criteria).length > 0 ? this.searchService.criteria : null;

		this.buildFacets(data);
	}

	private buildFacets(data: any): void {
		if (this.validFields.length == 0) {
			if (!this.userService.user.properties) return;
			this.getValidFields();
		}

		// Convenience model for lookup by name
		let validFieldsMap: any = {};
		this.validFields.forEach((field: any) => {
			validFieldsMap[field.name] = field;
		});

		if (!Array.isArray(data.facets)) {
			data.facets = [data.facets];
		}

		// uses a list of facet that needs to be preserved
		data.facets = data.facets.map((facet: any) => {
			// uses old facet if it's in criteria, if not uses the new facets
			let criteriaFacet = this.facets?.find((facetFind) => {
				return (this.criteria &&
					(this.criteria[facet.solrfieldname] || this.criteria[facet.name]))
					&& facetFind.name === facet.name
			});
			if (criteriaFacet) {
				return criteriaFacet;
			}

			var solrfieldname = facet.name;
			if (facet.name.endsWith("_s")) {
				var index = facet.name.lastIndexOf("_s");
				solrfieldname = facet.name.substring(0, index);
			} else if (facet.name.endsWith("_ss")) {
				var index = facet.name.lastIndexOf("_ss");
				solrfieldname = facet.name.substring(0, index);
			}

			if (!Array.isArray(facet.value)) {
				facet.value = facet.value == null ? [] : [facet.value];
			}
			facet.valid = validFieldsMap[solrfieldname] != null;

			var typeList = this.userService.user.properties.awfieldCollectionObjectList.filter((x: any) => { if (x.field == solrfieldname) return x.element; })
			if (typeList.length > 0 && Array.isArray(typeList[0].element)) {
				// hide values not in the user's field collection list
				facet.value.forEach((x: any) => {
					x.hide = true;
					for (var i = 0, iLen = typeList[0].element.length; i < iLen; i++) {
						try {
							if (typeList[0].element[i].value.toLowerCase() == x.value.toLowerCase()) {
								x.hide = false;
								break;
							}
						} catch (err) {
						}
					}
				})
			}

			facet.displayName =
				validFieldsMap[solrfieldname] && validFieldsMap[solrfieldname].displayName ?
					validFieldsMap[solrfieldname].displayName :
					solrfieldname;

			if (!this.multiFacetSearch) {
				facet.multiFaceted = false;
			} else if (this.multiFacetSearch && this.multiFacetBlacklist.length == 0) {
				facet.multiFaceted = true;
			} else {
				facet.multiFaceted = !this.multiFacetBlacklist.includes(facet.solrfieldname);
			}
			return facet;
		});

		if (this.facets && this.facets[0] && this.facets[0].dcname) {
			//we only try the dcname sort if it exists
			this.facets = data.facets.sort((a: any, b: any) => {
				if (validFieldsMap[a.name].dcname > validFieldsMap[b.name].dcname) {
					return 1;
				} else if (validFieldsMap[a.name].dcname < validFieldsMap[b.name].dcname) {
					return -1;
				} else {
					return 0;
				}
			});
		}

		// make sure only facets with a greater length of 0 is selected
		data.facets = data.facets.filter((facet: any) => {
			return facet.value.filter((value: any) => {
				return !value.hide
			}).length > 0
		})
		this.facets = [...data.facets];

		// Load the UI helper models with the facet state
		this.buildHelperModels();
	}

	private buildHelperModels(): void {
		let index = 0;
		this.facets.forEach((facet: any) => {
			this.facetfilter[facet.name] = '';
			this.focused[facet.name] = false;

			this.facetLimit[facet.name] = 5;

			let visibleCount = facet.value.filter((item: any) => !item.hide).length;
			this.facetVisibleCount[facet.name] = Math.min(visibleCount, this.maxLimit);

			this.expandedStates[index] = facet.valid;
			index++;
		});
	}

	filterByHidden(item: any): boolean {
		return !item.hide;
	}

	toggleFacet(facet: any, facetValue: any): void {
		this.triggerFacet(facet, facetValue, true, false);
	}

	/**
	 * Refines the search using the given facet value.
	 */
	triggerFacet(facet: any, facetValue: any, facetPreserved: boolean = false, isMultiValued: boolean = false): void {
		let tmpValue = facetValue.quotifiedValue || facetValue.value;

		if ((this.multiFacetSearch && facetPreserved) || isMultiValued) {
			//			this.facetPreserved = facetPreserved;
			let values = [];
			for (let i = 0; i < facet.value.length; i++) {
				if (facet.value[i].checked) {
					values.push(facet.value[i].quotifiedValue || facet.value[i].value);
				}
			}
			tmpValue = values.join(" OR ");
		}

		let currType = this.searchInputs.find(function(x) {
			return x.name == facet.name || x.name + "_s" == facet.name || x.name + "_ss" == facet.name
		});

		// Defensive check: facet not found in user's search fields
		if (!currType) {
			return;
		}

		let value = tmpValue;
		if (currType.type && currType.type.toUpperCase() == "LIST_MULTI") {
			value = [tmpValue];
		}
		else if (currType.type == "STRING") {
			value = [{ content: tmpValue }];
		}
		else if (currType.type == "DATE") {
			let handleMultiRangeDates = function() {
				console.log(tmpValue);

				value = [];
				let userInputs = tmpValue.split(" OR ");
				for (let i = 0; i < userInputs.length; i++) {
					let userInput = userInputs[i].split("-");
					value.push({
						startDate: dayjs(userInput[0]),
						endDate: dayjs(userInput[1])
					});
				}

				console.log(value);
			};

			let handleDate = function() {
				let userInput = tmpValue.split("-");
				if (userInput.length == 2) {
					value = {
						startDate: dayjs(userInput[0]),
						endDate: dayjs(userInput[1])
					};
				}
				else if (userInput.length == 6) {
					let firstString = userInput.splice(0, 3).join("-");
					let secString = userInput.splice(0, 3).join("-");
					value = {
						startDate: dayjs(firstString),
						endDate: dayjs(secString)
					}
				}
				else {
					value = {
						startDate: dayjs(userInput[0]),
						endDate: dayjs(userInput[0])
					}
				}
			};

			tmpValue.includes(" OR ") ? handleMultiRangeDates() : handleDate();
		}

		// NOTE:
		// searchField.updateFn and field.updateFn was added to searchInputs in srsField.directive.js
		// Now this is done in fields.service.ts

		let parentMask = this.searchService.updateValue(currType, { content: value });
		if (parentMask) {
			this.searchService.updateValue(currType.parent.searchField, { content: parentMask });
			currType.parent.searchField.hidden = false;
			currType.parent.searchField.updateFn(currType.parent.searchField, true);
		}

		currType.updateFn(currType, true);

		if (facet.name.endsWith("_ss") || facet.name.endsWith("_s")) {
			currType.solrstring = facet.name;
		}

		this.searchService.broadcastUserTriggeredCriteriaUpdateEvent(this.searchInputs, {});
	}

	private getValidFields(): void {
		this.validFields = this.userService.user.properties.awfieldObjectList.filter((x: any) => {
			return x.isAvailable;
		});
	}

	toggleExpand(index: number) {
		this.expandedStates[index] = !this.expandedStates[index];
	}

	/**
	 * Shows all values for the given facet.
	 */
	showAll(facet: any): void {
		const modalRef = this.ngbModal.open(AllFacetsComponent, {
			size: 'lg'
		});

		modalRef.componentInstance.facet = facet;

		modalRef.result.then(
			(result) => {
				if (result?.answer === 'ok') {
					this.triggerFacet(result.facet, {}, true, true);
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}
}